import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Textarea } from '../components/ui/textarea';
import { API_URL } from '../config';
import {
  Pill,
  Sparkles,
  Search,
  AlertTriangle,
  Info,
  Syringe,
  Droplet,
  Clock,
  Shield,
  Brain,
  Heart,
  Activity,
  Zap,
  Lock,
  Thermometer
} from 'lucide-react';
import { toast } from 'sonner';

export default function PrescriptionAdultePage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMed, setSelectedMed] = useState(null);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiResponse, setAiResponse] = useState('');
  const [loading, setLoading] = useState(false);

  // Organisation des médicaments par catégorie
  const medicationCategories = {
    analgesiques: {
      name: "Analgésiques / Antipyrétiques",
      icon: <Thermometer className="w-5 h-5" />,
      color: "bg-purple-500/10 text-purple-400 border-purple-500/30",
      medications: [
        {
          name: "Paracétamol (Perfalgan®)",
          indication: "Analgésie, fièvre",
          preparation: "Prêt à l'emploi (flacon ou poche)",
          posologie: "Adulte: 1g toutes les 4h. Personne âgée: 1g toutes les 3h",
          effetsSecondaires: "Réaction allergique, douleur et brûlure au site d'injection",
          voie: "IVL"
        },
        {
          name: "Morphine Chlorhydrate",
          indication: "Douleur intense",
          preparation: "À diluer ou aérosol",
          posologie: "Titration (2-3 mg toutes les 5 min) ou bolus (0,1 mg/kg pour douleur sévère)",
          effetsSecondaires: "Nausées, vomissements, sédation, dépression respiratoire, bradycardie, hypotension",
          voie: "IV / SC",
          alert: "Antalgique de palier 3 - Surveillance étroite"
        },
        {
          name: "Kétamine",
          indication: "Analgésie, colique néphrétique, traumatisme",
          preparation: "À diluer",
          posologie: "0,15 à 0,3 mg/kg",
          effetsSecondaires: "Vertiges, désorientation, dysphorie, hallucinations, dépression respiratoire",
          voie: "IV / Intranasale"
        },
        {
          name: "Kétoprofène (Profénid®)",
          indication: "Colique néphrétique, traumatisme, rhumatisme",
          preparation: "À diluer dans 100 mL NaCl 0,9%",
          posologie: "100 mg en IVL sur 20 min",
          effetsSecondaires: "Hypersensibilité, dyspepsie, douleur abdominale, nausées",
          voie: "IVL"
        },
        {
          name: "Néfopam Chlorhydrate (Acupan®)",
          indication: "Douleur modérée à sévère",
          preparation: "À diluer ou PSE",
          posologie: "IV poche: 20 mg toutes les 4h, max 120 mg/j. PSE: 100 mg sur 24h",
          effetsSecondaires: "Sueurs, nausées, somnolence, confusion, vertiges, tachycardie",
          voie: "IV / PSE"
        }
      ]
    },
    anticoagulants: {
      name: "Anticoagulants / Antithrombotiques",
      icon: <Droplet className="w-5 h-5" />,
      color: "bg-red-500/10 text-red-400 border-red-500/30",
      medications: [
        {
          name: "Héparine sodique",
          indication: "SCA, EP, AVC, FA",
          preparation: "À diluer",
          posologie: "Bolus puis PSE selon protocole",
          contrIndications: "Hémorragie active, AVC hémorragique, HTA non contrôlée, allergie héparine",
          effetsSecondaires: "Hémorragie, thrombopénie induite par l'héparine",
          voie: "IV / PSE",
          alert: "Surveillance plaquettes et TCA obligatoire"
        },
        {
          name: "Enoxaparine (Lovenox®)",
          indication: "STEMI < 12h, Thrombolyse",
          preparation: "Seringue pré-remplie prête à l'emploi",
          posologie: "< 75 ans: 30 mg IV + 1 mg/kg SC. > 75 ans: 0,75 mg/kg SC",
          effetsSecondaires: "Hémorragie, réaction allergique",
          voie: "IV + SC"
        },
        {
          name: "Acide acétyl salicylique (ASL)",
          indication: "SCA, AVC, Péricardite",
          preparation: "À reconstituer",
          posologie: "SCA/AVC: 250 mg IVD (2,5 mL). Péricardite: 1g (10 mL)",
          effetsSecondaires: "Hémorragie, nausées, vomissements, réaction d'hypersensibilité",
          voie: "IVD"
        },
        {
          name: "Acide Tranexamique (Exacyl®)",
          indication: "Trauma sévère, choc hémorragique",
          preparation: "À diluer",
          posologie: "1ère dose: 1g en IVL dans l'heure suivant le trauma. 2ème dose selon clinique",
          effetsSecondaires: "Troubles digestifs si injection trop rapide, convulsions",
          voie: "IVL",
          alert: "Administrer dans l'heure suivant le traumatisme"
        }
      ]
    },
    antiarythmiques: {
      name: "Antiarythmiques",
      icon: <Activity className="w-5 h-5" />,
      color: "bg-cyan-500/10 text-cyan-400 border-cyan-500/30",
      medications: [
        {
          name: "Amiodarone (Cordarone®)",
          indication: "AC (arrêt cardiaque), troubles du rythme",
          preparation: "Pur ou à diluer",
          posologie: "300 mg après 3ème CEE, 150 mg après 5ème CEE. Troubles du rythme: 300 mg en 30 min puis PSE",
          effetsSecondaires: "Phlébite, bradycardie, hypotension, nausées, thyroïdite",
          voie: "IV / PSE",
          alert: "Surveiller QTc et fonction thyroïdienne"
        },
        {
          name: "Adénosine (Krénosin®)",
          indication: "Conversion tachycardie jonctionnelle, aide diagnostique",
          preparation: "Pur",
          posologie: "1ère dose: 12 mg. 2ème dose: 18 mg après 2-3 min si inefficace",
          contrIndications: "Asthme sévère/BPCO, bloc AV de haut degré, hypotension sévère, QT long",
          effetsSecondaires: "Pause sinusale prolongée, syncope, bradycardie, dyspnée, flush facial",
          voie: "IVD FLASH",
          alert: "Injection rapide obligatoire + flush NaCl immédiat"
        },
        {
          name: "Esmolol (Brévibloc®)",
          indication: "AC, contrôle PA dans SCA < 12h, TSV",
          preparation: "Pur",
          posologie: "Dose de charge: 0,5 mg/kg sur 1 min. Entretien: 3-12 mg/kg/h",
          contrIndications: "Bradycardie < 50 bpm, bloc sévère, choc cardiogénique, asthme",
          effetsSecondaires: "Hypotension, céphalées, nausées, nécrose cutanée si extravasation",
          voie: "IV / PSE"
        },
        {
          name: "Lidocaïne (Xylocard®)",
          indication: "AC en rythme choquable si amiodarone indisponible",
          preparation: "Pur",
          posologie: "100 mg après 3ème CEE, 50 mg après 5ème CEE",
          voie: "IV"
        }
      ]
    },
    hypnotiques: {
      name: "Hypnotiques / Sédatifs",
      icon: <Brain className="w-5 h-5" />,
      color: "bg-indigo-500/10 text-indigo-400 border-indigo-500/30",
      medications: [
        {
          name: "Propofol (Diprivan®)",
          indication: "Sédation procédurale, induction et maintien d'anesthésie",
          preparation: "Pur",
          posologie: "Sédation: 0,8-1 mg/kg IVDL sur 30 sec. Personne âgée: demi-dose",
          effetsSecondaires: "Douleur au point d'injection, hypotension transitoire, dépression respiratoire, apnée",
          voie: "IVDL / PSE",
          alert: "Surveillance continue de la ventilation et PA"
        },
        {
          name: "Etomidate (Hypnomidate®)",
          indication: "Induction en séquence rapide (ISR)",
          preparation: "Pur",
          posologie: "0,3 mg/kg IVD. Possibilité de monter à 0,4-0,5 mg/kg en état de choc",
          contrIndications: "Insuffisance surrénalienne non compensée, enfant < 2 ans",
          effetsSecondaires: "Dyskinésie, myoclonies, hypotension, douleur veineuse, apnée",
          voie: "IVD"
        },
        {
          name: "Midazolam (Hypnovel®)",
          indication: "Sédation vigile, agitation, état de mal épileptique",
          preparation: "Pur ou à diluer",
          posologie: "Sédation: 1 mg en IVD titration toutes les 2-3 min. État de mal: 5 mg IV bolus puis PSE",
          effetsSecondaires: "Dépression respiratoire, apnée, hypotension, bradycardie, confusion",
          voie: "IV / Intranasale / PSE",
          alert: "Antidote: Flumazénil (Anexate®)"
        },
        {
          name: "Kétamine",
          indication: "Sédation procédurale, analgésie",
          preparation: "À diluer",
          posologie: "Faible dose analgésique: 0,15-0,3 mg/kg. Sédation: 0,8-1 mg/kg",
          effetsSecondaires: "Vertiges, désorientation, hallucinations, agitation, dépression respiratoire",
          voie: "IVDL / Intranasale"
        }
      ]
    },
    electrolytes: {
      name: "Électrolytes / Correcteurs",
      icon: <Zap className="w-5 h-5" />,
      color: "bg-yellow-500/10 text-yellow-400 border-yellow-500/30",
      medications: [
        {
          name: "Glucose 30%",
          indication: "Hypoglycémie sévère",
          preparation: "Pur",
          posologie: "Commencer par deux ampoules de 20 mL (12 g de glucose). Répéter selon glycémie capillaire",
          effetsSecondaires: "Réaction allergique, irritation au site de perfusion",
          voie: "IV"
        },
        {
          name: "Calcium chlorure",
          indication: "Hyperkaliémie menaçante, intoxication inhibiteurs calciques, hypocalcémie aiguë",
          preparation: "À diluer",
          posologie: "1g en IVL sur 10 min. IVD pour AC et traitement de cause",
          contrIndications: "Hypercalcémie, traitement digitalique (risque de troubles du rythme sévères)",
          effetsSecondaires: "Risque de nécrose tissulaire et veinotoxicité sévère",
          voie: "IVL / IVD",
          alert: "Préférer calcium gluconate si disponible (moins veinotoxique)"
        },
        {
          name: "Bicarbonate de sodium 8,4%",
          indication: "AC avec hyperkaliémie, intoxication grave drogues stabilisatrices de membrane",
          preparation: "Pur",
          posologie: "Intoxication: 250 mL, adapter selon QRS. AC: 50 mL IVD",
          effetsSecondaires: "Alcalose métabolique et hypokaliémie si apport excessif",
          voie: "IV"
        },
        {
          name: "Magnésium sulfate",
          indication: "Torsades de pointes, hypokaliémie sévère, asthme grave",
          preparation: "À diluer",
          posologie: "2g IVL sur 15 min. AC avec hypokaliémie: 2g IVDL sur 1-2 min",
          contrIndications: "Insuffisance rénale sévère, hypotension, bradycardie",
          effetsSecondaires: "Douleur au point d'injection, sensation de chaleur",
          voie: "IVL / IVDL"
        },
        {
          name: "Chlorure de potassium",
          indication: "Hypokaliémie sévère avec impossibilité de supplémenter per os",
          preparation: "À diluer",
          posologie: "1g en IVL sur 1h minimum, ou PSE (0,5g/50mL)",
          contrIndications: "Hyperkaliémie, acidose métabolique non traitée, insuffisance rénale sévère",
          effetsSecondaires: "AC, FV si injection trop rapide, chute de PA, arythmies",
          voie: "IVL / PSE",
          alert: "Ne JAMAIS injecter en IVD - Risque mortel"
        }
      ]
    },
    antidotes: {
      name: "Antidotes",
      icon: <Shield className="w-5 h-5" />,
      color: "bg-green-500/10 text-green-400 border-green-500/30",
      medications: [
        {
          name: "Naloxone (Narcan®)",
          indication: "Surdosage opioïdes avec dépression respiratoire",
          preparation: "Pur puis diluer pour PSE",
          posologie: "Titration: 1 mL toutes les 60 sec pour FR > 15/min. PSE selon titration",
          effetsSecondaires: "Tachycardie, HTA à fortes doses, agitation, anxiété, vomissements, syndrome de sevrage",
          voie: "IV / Intranasale / PSE",
          alert: "Durée d'action courte - Surveillance prolongée nécessaire"
        },
        {
          name: "Flumazénil (Anexate®)",
          indication: "Coma par surdosage en benzodiazépines",
          preparation: "Pur pour titration puis PSE",
          posologie: "Titration: 0,1 mg toutes les 30 sec jusqu'à réversion. PSE: 0,1-0,4 mg/h",
          contrIndications: "Co-intoxication avec agents pro-convulsivants, antécédents d'épilepsie, intoxication ADT",
          effetsSecondaires: "Arythmies ventriculaires, crises convulsives tonico-cloniques, agitation",
          voie: "IV / PSE"
        },
        {
          name: "Atropine Sulfate",
          indication: "Bradycardie, troubles de conduction AV, syndrome muscarinique",
          preparation: "Pur",
          posologie: "Bolus 0,5 mg titration toutes les 3-5 min. Max 3 mg",
          contrIndications: "Glaucome à angle fermé, adénome de prostate (sauf détresse vitale)",
          effetsSecondaires: "Sécheresse buccale, rétention urinaire, palpitations, tachycardie",
          voie: "IV"
        },
        {
          name: "Acétylcystéine (Hidonac®)",
          indication: "Intoxication au paracétamol",
          preparation: "À diluer",
          posologie: "Dose de charge: 150 mg/kg en 1h. 2ème dose: 50 mg/kg sur 4h. 3ème dose: 100 mg/kg sur 16h",
          effetsSecondaires: "Réactions anaphylactoïdes, vomissements, nausées",
          voie: "IVL",
          alert: "Efficacité maximale si débuté dans les 8h"
        }
      ]
    },
    anticonvulsivants: {
      name: "Anticonvulsivants",
      icon: <Zap className="w-5 h-5" />,
      color: "bg-pink-500/10 text-pink-400 border-pink-500/30",
      medications: [
        {
          name: "Clonazépam (Rivotril®)",
          indication: "État de mal épileptique, crise convulsive ≥ 5 min",
          preparation: "À diluer avec solvant",
          posologie: "0,015 mg/kg (1 mg pour 70kg) en IVD. Personne âgée: demi-dose. Répéter 1 fois après 5 min",
          contrIndications: "Allergie benzodiazépines, insuffisance respiratoire, SAS, myasthénie",
          effetsSecondaires: "Dyskinésie, myoclonies, hypotension, douleur veineuse, apnée",
          voie: "IVD"
        },
        {
          name: "Valproate de sodium (Dépakine®)",
          indication: "État de mal épileptique",
          preparation: "PSE, à diluer",
          posologie: "40 mg/kg (max 3g)",
          contrIndications: "Grossesse, femme en âge de procréer, hépatite aiguë/chronique, porphyrie hépatique",
          effetsSecondaires: "Tremblements, encéphalopathie, hyperammoniémie, troubles extrapyramidaux",
          voie: "PSE"
        },
        {
          name: "Phenobarbital (Gardénal®)",
          indication: "État de mal épileptique",
          preparation: "Reconstituer puis diluer pour PSE",
          posologie: "15 mg/kg. Administrer dose entière même si crise s'arrête",
          contrIndications: "Insuffisance respiratoire sévère",
          effetsSecondaires: "Altération de conscience, somnolence, agitation, nausées, vomissements",
          voie: "IV / PSE"
        }
      ]
    }
  };

  const handleAiAnalysis = async () => {
    if (!aiPrompt.trim()) {
      toast.error("Veuillez décrire la situation clinique");
      return;
    }

    setLoading(true);
    try {
      const context = selectedMed 
        ? `Médicament sélectionné: ${selectedMed.name}\n\nSituation clinique:\n${aiPrompt}`
        : `Situation clinique:\n${aiPrompt}`;

      const response = await fetch(`${API_URL}/api/ai/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prompt: context,
          context: "prescription_aide_adulte"
        })
      });

      if (!response.ok) throw new Error('Erreur lors de l\'analyse IA');

      const data = await response.json();
      setAiResponse(data.response);
      toast.success("Analyse IA terminée");
    } catch (error) {
      console.error('AI Analysis Error:', error);
      toast.error("Erreur lors de l'analyse IA");
      setAiResponse("Erreur lors de l'analyse. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  const filteredCategories = Object.entries(medicationCategories).reduce((acc, [key, category]) => {
    const filteredMeds = category.medications.filter(med =>
      med.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      med.indication.toLowerCase().includes(searchTerm.toLowerCase())
    );
    if (filteredMeds.length > 0) {
      acc[key] = { ...category, medications: filteredMeds };
    }
    return acc;
  }, {});

  return (
    <div className="space-y-6" data-testid="prescription-adulte-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold glow-text flex items-center gap-3">
            <Pill className="w-8 h-8 text-cyan-400" />
            Prescrire - Adulte
          </h1>
          <p className="text-[hsl(var(--text-dim))] mt-2">
            Guide interactif de prescription avec analyse IA
          </p>
        </div>
      </div>

      {/* Search */}
      <Card className="border-[hsl(var(--border))]">
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-3 w-5 h-5 text-[hsl(var(--text-dim))]" />
            <Input
              placeholder="Rechercher un médicament ou une indication..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Medications List */}
        <div className="lg:col-span-2 space-y-4">
          <Card className="border-[hsl(var(--border))]">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Pill className="w-5 h-5 text-cyan-400" />
                Médicaments par Catégorie
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Accordion type="single" collapsible className="space-y-2">
                {Object.entries(filteredCategories).map(([key, category]) => (
                  <AccordionItem key={key} value={key} className="border border-[hsl(var(--border))] rounded-lg px-4">
                    <AccordionTrigger className="hover:no-underline">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${category.color}`}>
                          {category.icon}
                        </div>
                        <div className="text-left">
                          <div className="font-semibold">{category.name}</div>
                          <div className="text-sm text-[hsl(var(--text-dim))]">
                            {category.medications.length} médicament(s)
                          </div>
                        </div>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="space-y-3 mt-3">
                        {category.medications.map((med, idx) => (
                          <div
                            key={idx}
                            className="p-4 bg-[hsl(var(--card-hover))] rounded-lg border border-[hsl(var(--border))] hover:border-cyan-500/50 transition-colors cursor-pointer"
                            onClick={() => setSelectedMed(med)}
                          >
                            <div className="flex items-start justify-between mb-2">
                              <h4 className="font-semibold text-white">{med.name}</h4>
                              {med.alert && (
                                <Badge variant="destructive" className="ml-2">
                                  <AlertTriangle className="w-3 h-3 mr-1" />
                                  Alert
                                </Badge>
                              )}
                            </div>
                            
                            <div className="space-y-2 text-sm">
                              <div className="flex gap-2">
                                <Info className="w-4 h-4 text-cyan-400 flex-shrink-0 mt-0.5" />
                                <div>
                                  <span className="text-[hsl(var(--text-dim))]">Indication:</span>{' '}
                                  <span className="text-white">{med.indication}</span>
                                </div>
                              </div>
                              
                              <div className="flex gap-2">
                                <Syringe className="w-4 h-4 text-purple-400 flex-shrink-0 mt-0.5" />
                                <div>
                                  <span className="text-[hsl(var(--text-dim))]">Posologie:</span>{' '}
                                  <span className="text-white">{med.posologie}</span>
                                </div>
                              </div>
                              
                              <div className="flex gap-2">
                                <Clock className="w-4 h-4 text-yellow-400 flex-shrink-0 mt-0.5" />
                                <div>
                                  <span className="text-[hsl(var(--text-dim))]">Voie:</span>{' '}
                                  <Badge variant="outline" className="ml-1">{med.voie}</Badge>
                                </div>
                              </div>

                              {med.alert && (
                                <Alert className="mt-2 bg-red-500/10 border-red-500/30">
                                  <AlertTriangle className="w-4 h-4 text-red-400" />
                                  <AlertDescription className="text-red-300 text-xs">
                                    {med.alert}
                                  </AlertDescription>
                                </Alert>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </CardContent>
          </Card>
        </div>

        {/* AI Analysis Panel */}
        <div className="space-y-4">
          {selectedMed && (
            <Card className="border-cyan-500/30 bg-cyan-500/5">
              <CardHeader>
                <CardTitle className="text-sm flex items-center gap-2">
                  <Pill className="w-4 h-4" />
                  Sélectionné
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <h3 className="font-bold text-white">{selectedMed.name}</h3>
                  <div className="text-sm space-y-2">
                    <div>
                      <span className="text-[hsl(var(--text-dim))]">Préparation:</span>
                      <p className="text-white">{selectedMed.preparation}</p>
                    </div>
                    {selectedMed.contrIndications && (
                      <div>
                        <span className="text-[hsl(var(--text-dim))] flex items-center gap-1">
                          <Lock className="w-3 h-3" />
                          Contre-indications:
                        </span>
                        <p className="text-red-300 text-xs">{selectedMed.contrIndications}</p>
                      </div>
                    )}
                    {selectedMed.effetsSecondaires && (
                      <div>
                        <span className="text-[hsl(var(--text-dim))] flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3" />
                          Effets secondaires:
                        </span>
                        <p className="text-yellow-300 text-xs">{selectedMed.effetsSecondaires}</p>
                      </div>
                    )}
                  </div>
                  <Button
                    onClick={() => setSelectedMed(null)}
                    variant="outline"
                    size="sm"
                    className="w-full"
                  >
                    Désélectionner
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          <Card className="border-[hsl(var(--border))]">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-cyan-400" />
                Analyse IA
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Textarea
                placeholder="Décrivez la situation clinique (âge, poids, pathologie, contre-indications...)&#10;&#10;Exemple: Patient 65 ans, 80kg, SCA ST+, PA 140/90, pas d'allergie connue"
                value={aiPrompt}
                onChange={(e) => setAiPrompt(e.target.value)}
                rows={6}
                className="resize-none"
              />
              
              <Button
                onClick={handleAiAnalysis}
                disabled={loading || !aiPrompt.trim()}
                className="w-full bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500"
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Analyse en cours...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Analyser avec IA
                  </>
                )}
              </Button>

              {aiResponse && (
                <div className="p-4 bg-[hsl(var(--card-hover))] rounded-lg border border-cyan-500/30">
                  <div className="flex items-center gap-2 mb-3">
                    <Brain className="w-5 h-5 text-cyan-400" />
                    <span className="font-semibold text-white">Recommandation IA</span>
                  </div>
                  <div className="text-sm text-white whitespace-pre-wrap leading-relaxed">
                    {aiResponse}
                  </div>
                  <Alert className="mt-3 bg-yellow-500/10 border-yellow-500/30">
                    <AlertTriangle className="w-4 h-4 text-yellow-400" />
                    <AlertDescription className="text-yellow-300 text-xs">
                      Cette analyse IA est une aide à la décision. Elle ne remplace pas le jugement clinique du médecin.
                    </AlertDescription>
                  </Alert>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
